
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ScrollButtons from '../../components/feature/ScrollButtons';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';
import { useNavigate } from 'react-router-dom';

export default function Home() {
  const navigate = useNavigate();

  const featuredProjects = [
    {
      id: 1,
      title: "Global Grace Children Homes",
      description: "We lovingly care for over 225+ children across 9 vibrant homes, each nurturing around 25+ little ones. These children are mostly orphans or some with a single parent who don't have a source of income.",
      image: "https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/d160f5f2ed9a9e2b57c0f27dc3affde6.jfif",
      raised: 2500,
      goal: 54000,
      location: "Andhra Pradesh, India"
    },
    {
      id: 2,
      title: "Global Grace Sewing Institute",
      description: "At our 5 well-established centers and 1 newly opened facility, we empower women with a 6-month free sewing program. Each batch trains 25 women in fundamental tailoring techniques blended with spiritual encouragement.",
      image: "https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/e8a751b811a1d125786e1fc827cb3e12.jfif",
      raised: 5000,
      goal: 50000,
      location: "Andhra Pradesh, India"
    },
    {
      id: 3,
      title: "Educational Scholarships",
      description: "Since 2022, we have provided scholarships to 77 bright students, from 10th grade through postgraduate studies. This scholarships help close the gap between potential and opportunity, empowering young minds to grow into tomorrow's leaders.",
      image: "https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/02de37af0138bc357b6ba21c1e03d8bc.jfif",
      raised: 1130,
      goal: 14600,
      location: "Andhra Pradesh, India"
    }
  ];

  const stats = [
    { number: "300K", label: "MEALS SERVED YEARLY", icon: "ri-heart-fill" },
    { number: "120K", label: "POUNDS OF FOOD", icon: "ri-building-fill" },
    { number: "515", label: "WOMEN TRAINED", icon: "ri-global-fill" },
    { number: "75K", label: "VITAMINS PROVIDED", icon: "ri-money-dollar-circle-fill" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section - Mobile Optimized */}
      <section 
        className="relative h-[70vh] md:h-screen bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/81befd3eca25397b762108f978c99951.jfif')`
        }}
      >
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-3xl">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 leading-tight">
                Building Hope, <br />
                <span className="text-blue-400">Changing Lives</span>
              </h1>
              <p className="text-base md:text-xl text-gray-200 mb-6 md:mb-8 leading-relaxed">
                Join us in creating sustainable solutions for communities in need. Together, we can make a lasting difference through education, healthcare, clean water, and economic empowerment programs worldwide.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Button 
                  variant="primary" 
                  size="md"
                  onClick={() => navigate('/donate')}
                >
                  <div className="flex items-center space-x-2">
                    <i className="ri-heart-fill"></i>
                    <span>Donate Now</span>
                  </div>
                </Button>
                <Button 
                  variant="outline" 
                  size="md"
                  onClick={() => navigate('/projects')}
                  className="border-white text-white hover-bg-white hover-text-gray-900"
                >
                  <div className="flex items-center space-x-2">
                    <i className="ri-eye-line"></i>
                    <span>View Projects</span>
                  </div>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Mobile Optimized */}
      <section className="py-8 md:py-16 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-2 md:mb-4 bg-blue-600 rounded-full flex items-center justify-center">
                  <i className={`${stat.icon} text-lg md:text-2xl text-white`}></i>
                </div>
                <div className="text-xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">{stat.number}</div>
                <div className="text-xs md:text-sm text-gray-600 font-medium px-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects - Mobile Optimized */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">Our Featured Projects</h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
              Discover the impactful projects we're currently working on around the world. Each project addresses critical needs and creates lasting positive change in communities.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {featuredProjects.map((project) => (
              <Card key={project.id} hover className="cursor-pointer" onClick={() => navigate(`/projects/${project.id}`)}>
                <div className="relative">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-40 md:h-48 object-cover object-top rounded-t-xl"
                  />
                  <div className="absolute top-3 right-3 bg-white px-2 py-1 rounded-full text-xs font-medium text-gray-700">
                    {project.location}
                  </div>
                </div>
                
                <div className="p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3">{project.title}</h3>
                  <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4 line-clamp-3">{project.description}</p>
                  
                  <div className="mb-3 md:mb-4">
                    <div className="flex justify-between text-xs md:text-sm text-gray-600 mb-2">
                      <span>Raised: ${project.raised.toLocaleString()}</span>
                      <span>Goal: ${project.goal.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${(project.raised / project.goal) * 100}%` }}
                      ></div>
                    </div>
                    <div className="text-xs md:text-sm text-gray-600 mt-1">
                      {Math.round((project.raised / project.goal) * 100)}% completed
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/donate?project=${project.id}`);
                      }}
                      className="flex-1 text-xs md:text-sm"
                    >
                      Support This
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/projects/${project.id}`);
                      }}
                      className="flex-1 text-xs md:text-sm"
                    >
                      Learn More
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8 md:mt-12">
            <Button 
              variant="outline" 
              size="md"
              onClick={() => navigate('/projects')}
            >
              View All Projects
            </Button>
          </div>
        </div>
      </section>

      {/* Impact Story - Mobile Optimized */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
                Creating Lasting Impact Together
              </h2>
              <p className="text-base md:text-lg text-gray-600 mb-4 md:mb-6 leading-relaxed">
                Every donation, every volunteer hour, and every shared story contributes to meaningful change. At Bible Believers Association, we stand on the values of faith, dignity, and hope—ensuring that support reaches where it matters most.
              </p>
              <div className="space-y-3 md:space-y-4">
                <div className="flex items-start space-x-3 md:space-x-4">
                  <div className="w-6 h-6 md:w-8 md:h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-heart-fill text-white text-xs md:text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm md:text-base">Faith-Rooted Impact</h4>
                    <p className="text-gray-600 text-sm md:text-base">From children's homes to water wells, every project is anchored in love and compassion, meeting both physical and spiritual needs.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 md:space-x-4">
                  <div className="w-6 h-6 md:w-8 md:h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-seedling-line text-white text-xs md:text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm md:text-base">Lasting Change</h4>
                    <p className="text-gray-600 text-sm md:text-base">Our initiatives—scholarships, sewing centers, leprosy and widows' care—are built to uplift communities for the long term, breaking cycles of poverty and dependence.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 md:space-x-4">
                  <div className="w-6 h-6 md:w-8 md:h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-star-line text-white text-xs md:text-sm"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm md:text-base">Empowering Hope</h4>
                    <p className="text-gray-600 text-sm md:text-base">By investing in people, we restore dignity, unlock potential, and raise up future leaders who will carry transformation into their communities.</p>
                  </div>
                </div>
              </div>
              <div className="mt-6 md:mt-8">
                <Button 
                  variant="primary" 
                  size="md"
                  onClick={() => navigate('/about')}
                >
                  Learn More About Us
                </Button>
              </div>
            </div>
            <div>
              <img 
                src="https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/8fb4b5100169f5636dfbbbca581a445a.jfif"
                alt="Community Impact"
                className="w-full h-64 md:h-96 object-cover object-center rounded-xl shadow-lg lg:object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action - Mobile Optimized */}
      <section className="py-12 md:py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 md:mb-6">
            Ready to Make a Difference?
          </h2>
          <p className="text-base md:text-xl text-blue-100 mb-6 md:mb-8 leading-relaxed">
            Join hands with us to make a lasting impact. Whether through giving, serving, or sharing the vision, every single act of kindness helps transform lives and bring hope to communities in need.
          </p>
          <div className="flex justify-center">
            <Button 
              variant="primary" 
              size="md"
              onClick={() => navigate('/donate')}
              className="!bg-white !text-blue-600 hover:!bg-gray-100 hover:!text-blue-600 shadow-lg"
            >
              <div className="flex items-center space-x-2">
                <i className="ri-heart-fill"></i>
                <span>Start Donating</span>
              </div>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollButtons />
    </div>
  );
}
